import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LayoutAdminHeader from '../components/LayoutAdminHeader';
import LayoutAdminFooter from '../components/LayoutAdminFooter';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const ServiceList = () => {
    const [services, setServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const [serviceName, setServiceName] = useState('');
    const [serviceDescription, setServiceDescription] = useState('');
    const [serviceImage, setServiceImage] = useState(null);

    const fetchServices = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");

const res = await axios.get(`${BASE_URL}/Service/GetAllServices`, {
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

            const data = res.data.data || [];

            const formatted = data.map((item, index) => ({
                ...item,
                id: item._id || index,
                imagePath: item.ServiceImage ? `${BASE_URL}/${item.ServiceImage.replace(/\\/g, '/')}` : '',
            }));

            setServices(formatted);
            setFilteredServices(formatted);
        } catch (err) {
            console.error('Failed to fetch services:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, []);

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearch(value);
        const filtered = services.filter(item =>
            item.ServiceName.toLowerCase().includes(value) ||
            item.ServiceDescription.toLowerCase().includes(value)
        );
        setFilteredServices(filtered);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this service?')) return;
        try {
            await axios.delete(`${BASE_URL}/Service/DeleteService/${id}`);
            fetchServices();
        } catch (err) {
            alert('Failed to delete service');
        }
    };

    const handleEdit = (row) => {
        setSelected(row);
        setServiceName(row.ServiceName);
        setServiceDescription(row.ServiceDescription);
        setServiceImage(null); // Only re-upload if changed
        setOpen(true);
    };

    const handleAdd = () => {
        setSelected(null);
        setServiceName('');
        setServiceDescription('');
        setServiceImage(null);
        setOpen(true);
    };

    const handleSave = async () => {
        if (!serviceName || !serviceDescription) {
            alert('Please fill in all required fields');
            return;
        }

        const formData = new FormData();
        formData.append('ServiceName', serviceName.trim());
        formData.append('ServiceDescription', serviceDescription.trim());
        if (serviceImage) {
            formData.append('ServiceImage', serviceImage);
        }

        try {
            if (selected) {
                await axios.put(`${BASE_URL}/Service/UpdateService/${selected._id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                await axios.post(`${BASE_URL}/Service/AddService`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }

            setOpen(false);
            fetchServices();
        } catch (err) {
            console.error('Save failed:', err);
            alert(err.response?.data?.message || 'ServiceName Already Exists');
        }
    };

    const columns = [
        {
            field: 'ServiceName',
            headerName: 'Service Name',
            flex: 1,
        },
        {
            field: 'ServiceDescription',
            headerName: 'Description',
            flex: 2,
        },
        {
            field: 'ServiceImage',
            headerName: 'Image',
            flex: 1,
            renderCell: (params) =>
                params.row.imagePath ? (
                    <img
                        src={params.row.imagePath}
                        alt="service"
                        style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                    />
                ) : (
                    'No Image'
                ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <>
                    <IconButton color="primary" onClick={() => handleEdit(params.row)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(params.row._id)}>
                        <DeleteIcon />
                    </IconButton>
                </>
            ),
        },
    ];

    return (
        <>
            <LayoutAdminHeader />
            <Box p={2}>
                <Typography variant="h5" gutterBottom>
                    Service Management
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={2}>
                    <TextField
                        label="Search"
                        value={search}
                        onChange={handleSearch}
                        placeholder="Search by service name or description"
                        fullWidth
                    />
                    <Button variant="contained" color="primary" onClick={handleAdd} sx={{ ml: 2 }}>
                        Add Service
                    </Button>
                </Box>

                <DataGrid
                    autoHeight
                    rows={filteredServices}
                    columns={columns}
                    pageSize={5}
                    rowsPerPageOptions={[5, 10]}
                    loading={loading}
                    disableSelectionOnClick
                />

                <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>{selected ? 'Edit Service' : 'Add Service'}</DialogTitle>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            label="Service Name"
                            fullWidth
                            value={serviceName}
                            onChange={(e) => setServiceName(e.target.value)}
                            required
                        />
                        <TextField
                            margin="dense"
                            label="Service Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={serviceDescription}
                            onChange={(e) => setServiceDescription(e.target.value)}
                            required
                        />
                        <Box mt={2}>
                            <Button variant="outlined" component="label">
                                {serviceImage ? 'Change Image' : 'Select Image'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) => {
                                        if (e.target.files.length > 0) {
                                            setServiceImage(e.target.files[0]);
                                        }
                                    }}
                                />
                            </Button>

                            {serviceImage && (
                                <Typography variant="body2" mt={1}>
                                    Selected: {serviceImage.name}
                                </Typography>
                            )}

                            {!serviceImage && selected?.imagePath && (
                                <Box mt={2}>
                                    <Typography variant="body2">Current Image:</Typography>
                                    <img
                                        src={selected.imagePath}
                                        alt="Current"
                                        style={{ width: 100, height: 100, objectFit: 'cover', marginTop: 8 }}
                                    />
                                </Box>
                            )}
                        </Box>

                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSave}>
                            {selected ? 'Update' : 'Add'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
            {/* <LayoutAdminFooter /> */}
        </>
    );
};

export default ServiceList;
